package com.iregained.site.components.sections.clinicians.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.corporate.styles.ActionButtonStyle
import com.iregained.site.components.sections.corporate.styles.ProcurementCardStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Elastic Pop
@Composable
private fun Modifier.elasticPop(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.8f).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun CliniciansProcurementSection(data: CliniciansProcurementSectionData = defaultCliniciansProcurementData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "clinicians-procurement-section"

    var isVisible by remember { mutableStateOf(false) }

    if (data.options.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.custom.lightGray)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            // Intersection Observer Wrapper (with flex center fix)
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element)
                            onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).elasticPop(isVisible, 0),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.sectionSubtitle.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).margin(bottom = 40.px).elasticPop(isVisible, 150).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.sectionSubtitle
                                        onDispose {}
                                    }
                                })
                    }

                    SimpleGrid(
                        numColumns = numColumns(base = 1, lg = 3),
                        modifier = Modifier.fillMaxWidth().maxWidth(1440.px).gap(30.px)
                    ) {
                        data.options.forEachIndexed { index, option ->
                            key(option.title) {
                                // Cascading delay: 300ms, 450ms, 600ms
                                ProcurementCard(option, Modifier.elasticPop(isVisible, 300 + (index * 150)))
                            }
                        }
                    }

                    if (data.footerDisclaimer.isNotBlank()) {
                        SpanText(
                            text = data.footerDisclaimer,
                            modifier = Modifier.margin(top = 40.px).fontSize(15.px).color(palette.custom.darkText)
                                .opacity(70.percent).textAlign(TextAlign.Center).elasticPop(isVisible, 800)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ProcurementCard(option: ProcurementOptionData, modifier: Modifier = Modifier) {
    val palette = ColorMode.current.toSitePalette()

    val cardModifier =
        ProcurementCardStyle.toModifier().then(modifier).fillMaxWidth().minHeight(560.px).borderRadius(12.px)
            .backgroundColor(Colors.White)
            .boxShadow(0.px, 10.px, 30.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.08f))
            .thenIf(option.isFeatured, Modifier.border(2.px, LineStyle.Solid, palette.brand.primary))

    Box(modifier = cardModifier) {
        Column(
            modifier = Modifier.fillMaxSize().padding(32.px),
            verticalArrangement = Arrangement.SpaceBetween,
            horizontalAlignment = Alignment.Start
        ) {
            Column(modifier = Modifier.fillMaxWidth()) {
                if (option.badgeText.isNotBlank()) {
                    Box(
                        modifier = Modifier.margin(bottom = 16.px).borderRadius(20.px).backgroundColor(
                            if (option.isFeatured) palette.brand.primarySoft else palette.custom.darkText.toRgb()
                                .copyf(alpha = 0.1f)
                        ).padding(topBottom = 6.px, leftRight = 16.px)
                    ) {
                        SpanText(
                            text = option.badgeText,
                            modifier = Modifier.color(if (option.isFeatured) palette.brand.primary else palette.custom.darkText)
                                .fontSize(13.px).fontWeight(FontWeight.Bold).letterSpacing(1.px)
                        )
                    }
                }

                SpanText(
                    text = option.title,
                    modifier = Modifier.color(palette.brand.primary).fontWeight(FontWeight.ExtraBold).fontSize(25.px)
                        .margin(bottom = 12.px)
                )

                SpanText(
                    text = option.targetProfile,
                    modifier = Modifier.color(palette.custom.darkText).fontSize(18.px).fontWeight(FontWeight.Medium)
                        .lineHeight(1.4).margin(bottom = 20.px)
                )

                Box(
                    modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(palette.custom.divider)
                        .margin(bottom = 16.px)
                )

                Div(
                    attrs = Modifier.color(palette.custom.darkText).fontSize(18.px).lineHeight(1.5)
                        .margin(bottom = 20.px).toAttrs {
                            ref { el -> el.innerHTML = option.coreAdvantage; onDispose {} }
                        })

                // UNIFORM BULLET RENDERING LOOP
                Column(modifier = Modifier.gap(12.px).margin(bottom = 24.px)) {
                    option.inclusions.forEach { itemHtml ->
                        key(itemHtml) {
                            Row(verticalAlignment = Alignment.Top) {
                                Box(
                                    modifier = Modifier.margin(top = 7.px, right = 10.px).size(6.px).minSize(6.px)
                                        .flexShrink(0).borderRadius(50.percent).backgroundColor(palette.brand.primary)
                                )
                                Div(
                                    attrs = Modifier.color(palette.custom.darkText).fontSize(18.px).lineHeight(1.3)
                                        .toAttrs {
                                            ref { el -> el.innerHTML = itemHtml; onDispose {} }
                                        })
                            }
                        }
                    }
                }
            }

            if (option.buttonText.isNotBlank() && option.linkPath.isNotBlank()) {
                Link(
                    path = option.linkPath,
                    modifier = Modifier.fillMaxWidth().textDecorationLine(TextDecorationLine.None)
                ) {
                    Box(
                        modifier = ActionButtonStyle.toModifier().fillMaxWidth().borderRadius(8.px)
                            .backgroundColor(palette.brand.primary).padding(topBottom = 12.px).cursor(Cursor.Pointer),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            text = option.buttonText,
                            modifier = Modifier.color(Colors.White).fontSize(20.px).fontWeight(FontWeight.Bold)
                        )
                    }
                }
            }
        }
    }
}

// --- CMS DATA MODEL ---
data class ProcurementOptionData(
    val title: String,
    val badgeText: String,
    val targetProfile: String,
    val coreAdvantage: String,
    val inclusions: List<String>,
    val buttonText: String,
    val linkPath: String,
    val isFeatured: Boolean = false
)

data class CliniciansProcurementSectionData(
    val sectionId: String? = null,
    val sectionHeader: String,
    val sectionSubtitle: String,
    val footerDisclaimer: String,
    val options: List<ProcurementOptionData>
)

// --- REGION SWITCHBOARD ---

private val usCliniciansData = CliniciansProcurementSectionData(
    sectionId = "clinicians-procurement-section",
    sectionHeader = "FLEXIBLE CLINICAL PROCUREMENT PATHWAYS",
    sectionSubtitle = "Align your clinical technology investment with your facility’s budget cycles. Choose from capital ownership, commercial leasing, or scalable rental models backed by our US lending partners.",
    footerDisclaimer = "NOTE: Commercial financing and leasing programs administered in partnership with secure US lending providers. Subject to standard corporate credit approval.",
    options = listOf(
        ProcurementOptionData(
            title = "Capital Purchase",
            badgeText = "DIRECT OWNERSHIP",
            targetProfile = "Established hospitals & clinics with approved capital equipment budgets.",
            coreAdvantage = "Maximize <b>long-term facility ROI</b>, asset ownership, and leverage potential <b>Section 179 tax deduction</b> benefits.",
            inclusions = listOf(
                "<b>Complete System Suite:</b> Hardware & advanced clinical software included",
                "<b>Comprehensive Protection:</b> Standard multi-year manufacturer warranty",
                "<b>Certified Execution:</b> Full staff clinical training certification included"
            ),
            buttonText = "Request Quote",
            linkPath = "/contact",
            isFeatured = false
        ), ProcurementOptionData(
            title = "Monthly Lease",
            badgeText = "LEASE TO OWN",
            targetProfile = "Private clinics & growing rehab practices looking to preserve upfront liquid capital.",
            coreAdvantage = "<b>Predictable operational expenditure:</b> Preserve liquid cash flow with flexible monthly structures.",
            inclusions = listOf(
                "<b>Scalable lifecycle terms:</b> Configurable continuous multi-year commercial leasing agreements",
                "<b>Streamlined Approval:</b> Efficient digital corporate pre-qualification application routing",
                "<b>End-of-Term Scalability:</b> Retain clear hardware refresh, upgrade, or permanent buyout pathways"
            ),
            buttonText = "Apply for B2B Leasing",
            linkPath = "/financing#financing-partners-section",
            isFeatured = false
        ), ProcurementOptionData(
            title = "Facility Rental",
            badgeText = "SHORT-TERM FLEXIBILITY",
            targetProfile = "Facilities seeking low-risk clinical evaluation pilots or managing temporary patient surges.",
            coreAdvantage = "Immediate clinical integration with <b>zero long-term balance sheet liabilities</b>.",
            inclusions = listOf(
                "<b>Predictable scaling:</b> Fixed monthly commercial rental terms",
                "<b>Turnkey Logistics:</b> Direct premium shipping, unboxing, setup, and teardown",
                "<b>Continuous Operations:</b> Full maintenance and calibration coverage included"
            ),
            buttonText = "Inquire About Rentals",
            linkPath = "/contact",
            isFeatured = false
        )
    )
)

private val canadaCliniciansData = CliniciansProcurementSectionData(
    sectionId = "clinicians-procurement-section",
    sectionHeader = "FLEXIBLE CLINICAL PROCUREMENT PATHWAYS",
    sectionSubtitle = "Align your clinical technology investment with your facility’s budget cycles. Choose from capital ownership, commercial leasing, or scalable rental models backed by our Canadian financing partners.",
    footerDisclaimer = "NOTE: Commercial financing and leasing programs administered in partnership with secure Canadian lending providers. Subject to standard corporate credit approval.",
    options = listOf(
        ProcurementOptionData(
            title = "Capital Purchase",
            badgeText = "DIRECT OWNERSHIP",
            targetProfile = "Established hospitals & clinics with approved capital equipment budgets.",
            coreAdvantage = "Maximize <b>long-term facility ROI</b>, asset ownership, and leverage <b>Capital Cost Allowance (CCA)</b> tax benefits.",
            inclusions = listOf(
                "<b>Complete System Suite:</b> Hardware & advanced clinical software included",
                "<b>Comprehensive Protection:</b> Standard multi-year manufacturer warranty",
                "<b>Certified Execution:</b> Full staff clinical training certification included"
            ),
            buttonText = "Request Quote",
            linkPath = "/contact",
            isFeatured = false
        ), ProcurementOptionData(
            title = "Commercial Lease",
            badgeText = "LEASE TO OWN",
            targetProfile = "Private clinics & growing rehab practices looking to preserve upfront liquid capital.",
            coreAdvantage = "<b>Predictable operational expenditure:</b> Preserve liquid cash flow with flexible monthly structures and potential corporate tax deduction advantages.",
            inclusions = listOf(
                "<b>Scalable lifecycle terms:</b> Configurable continuous multi-year commercial leasing agreements",
                "<b>Streamlined Approval:</b> Efficient digital corporate pre-qualification application routing",
                "<b>End-of-Term Scalability:</b> Retain clear hardware refresh, upgrade, or permanent buyout pathways"
            ),
            buttonText = "Apply for B2B Leasing",
            linkPath = "/financing#financing-partners-section",
            isFeatured = false
        ), ProcurementOptionData(
            title = "Facility Rental",
            badgeText = "SHORT-TERM FLEXIBILITY",
            targetProfile = "Facilities seeking low-risk clinical evaluation pilots or managing temporary patient surges.",
            coreAdvantage = "Immediate clinical integration with <b>zero long-term balance sheet liabilities</b>.",
            inclusions = listOf(
                "<b>Predictable scaling:</b> Fixed monthly commercial rental terms",
                "<b>Turnkey Logistics:</b> Direct premium shipping, unboxing, setup, and teardown",
                "<b>Continuous Operations:</b> Full maintenance and calibration coverage included"
            ),
            buttonText = "Inquire About Rentals",
            linkPath = "/contact",
            isFeatured = false
        )
    )
)

private val indiaCliniciansData = CliniciansProcurementSectionData(
    sectionId = "clinicians-procurement-section",
    sectionHeader = "CLINICAL PROCUREMENT & FINANCING",
    sectionSubtitle = "Optimize your CAPEX or OPEX strategy. Choose from direct acquisition, corporate EMIs, or scalable rental models backed by our specialized medical financing partners in India.",
    footerDisclaimer = "*NOTE: Corporate EMI and leasing programs administered in partnership with secure lending providers. Subject to standard credit approval.",
    options = listOf(
        ProcurementOptionData(
            title = "Capital Procurement",
            badgeText = "CAPEX MODEL",
            targetProfile = "Established hospitals & clinics with approved capital equipment budgets.",
            coreAdvantage = "Maximize <b>long-term facility ROI</b>, <b>asset ownership</b>, and leverage full equipment depreciation tax benefits under <b><a href='https://www.incometaxindia.gov.in/w/section-35ad-19#:~:text=(ab)-,on%20or%20after%20the%201st%20day%20of%20April%2C%202010%2C%20where,(ac)' target='_blank'>Section 35D of the Income Tax Act.</a></b>",
            inclusions = listOf(
                "<b>Complete System Suite:</b> Hardware & advanced clinical software included",
                "<b>Comprehensive Protection:</b> Standard multi-year manufacturer warranty",
                "<b>Certified Execution:</b> Full staff clinical training certification included"
            ),
            buttonText = "Request Quote",
            linkPath = "/contact",
            isFeatured = false
        ), ProcurementOptionData(
            title = "Corporate EMI / Lease",
            badgeText = "OPEX MODEL",
            targetProfile = "Private clinics & growing rehab practices looking to preserve upfront liquid capital.",
            coreAdvantage = "<b>Predictable operational expenditure:</b> Preserve liquid cash flow with flexible monthly EMI structures.",
            inclusions = listOf(
                "<b>Scalable lifecycle terms:</b> Configurable continuous multi-year commercial agreements",
                "<b>Streamlined Approval:</b> Efficient digital corporate pre-qualification application routing",
                "<b>End-of-Term Scalability:</b> Retain clear hardware refresh, upgrade, or permanent buyout pathways"
            ),
            buttonText = "Apply for Corporate EMI",
            linkPath = "/financing#financing-partners-section",
            isFeatured = false
        ), ProcurementOptionData(
            title = "Short-Term Rental",
            badgeText = "PILOT DEPLOYMENT",
            targetProfile = "Facilities seeking low-risk clinical evaluation pilots or managing temporary patient surges.",
            coreAdvantage = "Immediate clinical integration with <b>zero long-term balance sheet liabilities</b>.",
            inclusions = listOf(
                "<b>Predictable scaling:</b> Fixed monthly commercial rental terms",
                "<b>Turnkey Logistics:</b> Direct premium shipping, unboxing, setup, and teardown",
                "<b>Continuous Operations:</b> Full maintenance and calibration coverage included"
            ),
            buttonText = "Inquire About Rentals",
            linkPath = "/contact",
            isFeatured = false
        )
    )
)

val defaultCliniciansProcurementData: CliniciansProcurementSectionData
    get() = when (com.iregained.site.region.RegionConfig.currentRegion) {
        com.iregained.site.region.Region.US -> usCliniciansData
        com.iregained.site.region.Region.INDIA -> indiaCliniciansData
        com.iregained.site.region.Region.CANADA -> canadaCliniciansData
    }